import React from "react";
import { Phone, MessageSquare, Mail, MapPin, Clock, Train, AlertCircle, Info } from "lucide-react";
import { Container, Button, Eyebrow, SectionHeading } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { CTASection } from "@/components/sections";
import { useSeo } from "@/lib/seo";
import { business } from "@/data/site";

const InfoRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-4 border-b border-[var(--border)] py-4">
    <Icon size={18} className="mt-0.5 shrink-0 text-[var(--primary)]" />
    <div className="text-sm text-[var(--fg)]/85">{children}</div>
  </div>
);

export default function Contact() {
  useSeo({
    title: "Contact & Location | Meridian Dental Studio, Hell's Kitchen NYC",
    description: "Visit Meridian Dental Studio at 555 West 42nd Street, Hell's Kitchen, Manhattan. Call or text (212) 555-0142, get directions, hours and same-day emergency dental care.",
    path: "/contact",
  });

  return (
    <>
      <section className="pt-36 pb-12 sm:pt-44">
        <Container>
          <Reveal><Eyebrow>Contact & Location</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 max-w-3xl font-heading text-5xl font-light leading-[0.98] tracking-tight text-[var(--fg)] sm:text-6xl md:text-7xl text-balance">
              Come see us in Hell's Kitchen.
            </h1>
          </Reveal>
        </Container>
      </section>

      <section className="pb-24 sm:pb-32">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button size="lg" href={business.phoneHref} data-testid="contact-call-btn"><Phone size={16} /> Call {business.phoneDisplay}</Button>
                <Button size="lg" variant="outline" href={business.smsHref} data-testid="contact-text-btn"><MessageSquare size={16} /> Text Us</Button>
              </div>

              <div className="mt-8">
                <InfoRow icon={MapPin}>
                  <span className="font-medium text-[var(--fg)]">{business.address1}</span><br />
                  {business.address2}<br />
                  <span className="text-[var(--muted-fg)]">{business.crossStreets}</span>
                </InfoRow>
                <InfoRow icon={Mail}>
                  <a href={business.emailHref} className="hover:text-[var(--primary)]">{business.email}</a>
                </InfoRow>
                <InfoRow icon={Clock}>
                  <div className="space-y-1">
                    {business.hours.map((h) => (
                      <div key={h.day} className="flex justify-between gap-8">
                        <span>{h.day}</span><span className="text-[var(--muted-fg)]">{h.time}</span>
                      </div>
                    ))}
                  </div>
                </InfoRow>
                <InfoRow icon={Train}>
                  <div className="space-y-1.5">
                    {business.transit.map((t) => <div key={t}>{t}</div>)}
                  </div>
                </InfoRow>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-5">
                  <div className="flex items-center gap-2 text-[var(--primary)]"><AlertCircle size={17} /><span className="text-sm font-medium">Dental emergency?</span></div>
                  <p className="mt-2 text-sm text-[var(--fg)]/80">We hold same-day slots for urgent care. Call us and say it's urgent — we'll get you in fast.</p>
                </div>
                <div className="rounded-2xl border border-[var(--border)] p-5">
                  <div className="flex items-center gap-2 text-[var(--fg)]"><Info size={17} className="text-[var(--primary)]" /><span className="text-sm font-medium">New patients</span></div>
                  <p className="mt-2 text-sm text-[var(--muted-fg)]">Arrive 10 minutes early to complete intake. Please give 24 hours' notice for cancellations to avoid a fee.</p>
                </div>
              </div>
            </div>

            <div>
              <SectionHeading eyebrow="Quick inquiry" title="Send us a message." className="mb-8" />
              <ContactForm />
            </div>
          </div>

          <Reveal>
            <div className="mt-14 overflow-hidden rounded-2xl border border-[var(--border)]">
              <iframe
                title="Meridian Dental Studio location map"
                src={business.mapEmbed}
                width="100%"
                height="420"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </Container>
      </section>

      <CTASection eyebrow="Prefer to book online?" title="Reserve your appointment in under a minute." />
    </>
  );
}
